import type { BreadcrumbItem } from '@common/Breadcrumb/Breadcrumb';
import type { AttributeDefinitionFragment, Teasers } from '@generated/graphql';
import type { WidenMediaElement } from '@libs/widen-utils';
import type { NodeContact } from '@models/api/node-contact.types';
import type {
  MinimalProduct,
  MinimalProductWithVariantAxesAttributes,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import type { CertificateIcon } from '@models/graphql';
import { getProductImages } from '@page-helpers/getProductImages';
import type { ProductPageProps } from '@product/ProductPage/ProductPage';
import type { GetState, SetState } from 'zustand';
import type { ProductPageStore } from './useProductPageStore';
import { findProductWithLowestPrice, getProductTitle } from '@libs/product.helper';

export interface ProductDataSliceData {
  allProducts: MinimalProductWithVariantAxesAttributes[];
  doApplicationExamplesExist: boolean;
  modelKey: string;
  echainAttributeDefinitions: AttributeDefinitionFragment[];
  breadcrumbItems: BreadcrumbItem[];
  certificateIcons: CertificateIcon[];
  contact: NodeContact | null;
  currentProduct: ProductFragmentEnhanced;
  onlineToolsDefinition: Teasers[];
  primaryCategoryCode: string;
  productImages: {
    [x: string]: WidenMediaElement[];
  };
  buyBoxReady: boolean;
}

export interface ProductDataSlice extends ProductDataSliceData {
  getCurrentProduct: () => ProductFragmentEnhanced;
  getProductWithLowestPrice: () => MinimalProduct;
  setAllProducts: (allProducts: MinimalProductWithVariantAxesAttributes[]) => void;
  setDoApplicationExamplesExist: (doApplicationExamplesExist: boolean) => void;
  setModelKey: (modelKey: string) => void;
  setBreadcrumbItems: (breadcrumbItems: BreadcrumbItem[]) => void;
  setCertificateIcons: (certificateIcons: CertificateIcon[]) => void;
  setContact: (contact: NodeContact | null) => void;
  setCurrentProduct: (currentProduct: ProductFragmentEnhanced) => void;
  setOnlineToolsDefinition: (onlineToolsDefinition: Teasers[]) => void;
  setPrimaryCategoryCode: (code: string) => void;
  setProductImages: (productImages: { [x: string]: WidenMediaElement[] }) => void;
  setBuyBoxReady: (ready: boolean) => void;
}

export const createProductDataSlice = (
  set: SetState<ProductPageStore>,
  get: GetState<ProductPageStore>,
  initialData: ProductPageProps & Partial<ProductDataSliceData>
) =>
  ({
    clear: () => {},
    allProducts: initialData.allProducts,
    doApplicationExamplesExist: initialData.doApplicationExamplesExist,
    modelKey: initialData.modelKey,
    echainAttributeDefinitions: initialData.echainAttributeDefinitions,
    breadcrumbItems: initialData.breadcrumbItems,
    certificateIcons: initialData.certificateIcons,
    contact: initialData.contact,
    currentProduct: initialData.currentProduct ?? initialData.allProducts?.[0],
    onlineToolsDefinition: initialData.onlineToolsDefinition,
    primaryCategoryCode: initialData.primaryCategoryCode,
    productImages: initialData.currentProduct ? getProductImages([initialData.currentProduct], initialData.locale) : [],
    title: getProductTitle(initialData.currentProduct),
    buyBoxReady: false,

    getCurrentProduct: () => get().currentProduct,
    getProductWithLowestPrice: () => findProductWithLowestPrice(get().allProducts),
    setAllProducts: allProducts => {
      set(state => ({
        ...state,
        allProducts,
      }));
    },
    setDoApplicationExamplesExist: doApplicationExamplesExist => {
      set(state => ({
        ...state,
        doApplicationExamplesExist,
      }));
    },
    setModelKey: modelKey => {
      set(state => ({
        ...state,
        modelKey,
      }));
    },
    setCertificateIcons: certificateIcons => {
      set(state => ({
        ...state,
        certificateIcons,
      }));
    },
    setContact: contact => {
      set(state => ({
        ...state,
        contact,
      }));
    },
    setCurrentProduct: currentProduct => {
      set(state => ({
        ...state,
        currentProduct,
      }));
    },

    setPrimaryCategoryCode: code => {
      set(state => ({
        ...state,
        primaryCategoryCode: code,
      }));
    },
    setProductImages: productImages => {
      set(state => ({
        ...state,
        productImages,
      }));
    },
    setOnlineToolsDefinition: onlineToolsDefinition => {
      set(state => ({
        ...state,
        onlineToolsDefinition,
      }));
    },
    setBreadcrumbItems: breadcrumbItems => {
      set(state => ({
        ...state,
        breadcrumbItems,
      }));
    },
    setBuyBoxReady: buyBoxReady => {
      set(state => ({
        ...state,
        buyBoxReady,
      }));
    },
  } as ProductDataSlice);
